import { UnsupportedFormatError } from "../errors/file-errors";
import { CorsBlockedError, InvalidURLError, NetworkError } from "../errors/network-errors";
import { ErrorUtilities } from "./error.utilities";
import { URLUtilities } from "./url.utilities";

/**
 * Default timeout duration, in milliseconds, applied to network `fetch` operations.
 *
 * This value represents 15 seconds and is used to prevent requests from hanging indefinitely.
 * Adjust as needed based on expected API responsiveness, network conditions, or user experience requirements.
 *
 * Intended usage:
 * - As a guard for long-running HTTP requests.
 * - In utilities or wrappers that support aborting a `fetch` via `AbortController`.
 *
 * Note: When modifying, ensure dependent logic (e.g., retry strategies or progress indicators) remains consistent.
 */
const DEFAULT_FETCH_TIMEOUT_MS = 15000;

/**
 * A collection of browser-side image utilities for fetching, validating, and inspecting images.
 *
 * Provides methods to:
 * - Fetch an image as a Blob or File with strict validation, CORS-aware error handling, and timeouts.
 * - Convert fetched blobs to data URLs and object URLs.
 * - Read intrinsic image dimensions and compute aspect ratios.
 * - Assemble comprehensive image metadata in a single call.
 * - Safely derive filenames from URLs.
 *
 * Key functionality:
 * - `fetchImageWithInfo(url, filename?)`: Fetches an image and returns a bundle containing the File, Blob, data URL,
 *   object URL, width, height, aspect ratio, size, MIME type, and name.
 * - `fetchImageAsFile(url, filename?)`: Fetches and wraps an image Blob into a File, optionally inferring a name and extension.
 * - `fetchImageAsBlob(url)`: Robust image fetch with input validation, `AbortController` timeout, CORS detection,
 *   HTTP status checks, and content-type verification (headers and Blob.type).
 * - `getImageSize(url)`: Loads the image to read its intrinsic width and height.
 * - `calculateAspectRatio(width, height)`: Computes width/height, rejecting zero height.
 * - `getImageInfo(url)`: Returns width, height, and aspect ratio.
 * - `extractFilenameFromUrl(url)`: Extracts a filename from a URL path, with a safe fallback.
 *
 * Error handling and validation:
 * - Validates URL presence and format before requests.
 * - Differentiates timeouts, CORS/network rejections, and HTTP errors.
 * - Ensures fetched content is an image via `Content-Type` header and Blob.type checks.
 *
 * Throws:
 * - `InvalidURLError` — When the URL is missing or cannot be parsed.
 * - `NetworkError` — On timeout/abort or non-2xx HTTP responses.
 * - `CorsBlockedError` — When the browser blocks the request due to CORS.
 * - `UnsupportedFormatError` — When the response is not an image.
 * - `Error` — For general failures in size reading or blob-to-data URL conversion.
 *
 * Notes:
 * - Requires a browser environment (uses `fetch`, `AbortController`, `File`, `Blob`, `URL`, `Image`, `FileReader`).
 * - Uses an external `DEFAULT_FETCH_TIMEOUT_MS` and `ErrorUtilities` helpers for timing and error classification.
 */
export class ImageUtilities {

  /**
   * Fetches an image and returns comprehensive information about it
   * @param url - The URL of the image to fetch
   * @param filename - Optional filename
   * @returns Promise with complete image information
   */
  static async fetchImageWithInfo(url: string, filename?: string): Promise<{
    file: File;
    blob: Blob;
    dataUrl: string;
    width: number;
    height: number;
    aspectRatio: number;
    size: number;
    type: string;
    name: string;
  }> {
    const file = await this.fetchImageAsFile(url, filename);
    const blob = file as Blob;
    const dataUrl = await this.blobToDataUrl(blob);
    const { width, height, aspectRatio } = await this.getImageInfo(dataUrl);

    return {
      file,
      blob,
      dataUrl,
      width,
      height,
      aspectRatio,
      size: file.size,
      type: file.type,
      name: file.name
    };
  }

  /**
   * Fetches an image from URL and converts it to a File object
   * @param url - The URL of the image to fetch
   * @param filename - Optional filename, will be extracted from URL if not provided
   * @returns Promise that resolves to a File object
   */
  static async fetchImageAsFile(url: string, filename?: string): Promise<File> {
    const blob = await this.fetchImageAsBlob(url);

    // Extract filename from URL if not provided
    if (!filename) {
      const urlPath = new URL(url).pathname;
      filename = urlPath.split('/').pop() || 'image';

      // Add extension if missing
      if (!filename.includes('.')) {
        const mimeType = blob.type;
        const extension = mimeType.split('/')[1] || 'png';
        filename += `.${extension}`;
      }
    }

    return new File([blob], filename, { type: blob.type });
  }


  /**
   * Fetches an image from a given URL and returns it as a Blob, with robust input validation,
   * CORS/network error handling, timeout control, and content-type verification.
   *
   * Behavior:
   * - Validates the input URL string and its format before making a request.
   * - Uses an AbortController to enforce a default timeout (`DEFAULT_FETCH_TIMEOUT_MS`).
   * - Differentiates between timeout aborts, likely CORS rejections, and other network errors.
   * - Verifies the `Content-Type` header and the resulting `Blob.type` to ensure the response is an image.
   *
   * @param url - The absolute or relative URL of the image to fetch.
   * @returns A promise that resolves to an image Blob when the request succeeds and the content is valid.
   *
   * @throws {InvalidURLError} If the URL is missing or cannot be parsed.
   * @throws {NetworkError} If the request times out, is aborted, or the HTTP status is not OK (non-2xx).
   * @throws {CorsBlockedError} If the browser blocks the request due to CORS policy.
   * @throws {UnsupportedFormatError} If the response is not an image based on headers or Blob type.
   * @throws {Error} For other unexpected failures, preserving the original error message where possible.
   */
  static async fetchImageAsBlob(url: string): Promise<Blob> {
    if (!url || typeof url !== 'string' || !URLUtilities.isValidUrl(url)) {
      throw new InvalidURLError('URL is required');
    }

    let parsedURL: URL;
    try {
      parsedURL = new URL(url);
    } catch {
      throw new InvalidURLError('URL is not valid');
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), DEFAULT_FETCH_TIMEOUT_MS);

    let response: Response | null = null;
    try {
      response = await fetch(parsedURL.toString(), {
        signal: controller.signal
      });
    } catch (error: unknown) {
      clearTimeout(timeoutId);

      // Abort signals produce a DOMException in some browsers, TypeError in others
      if (ErrorUtilities.isAbortError(error)) {
        throw new NetworkError(`Request timed out after ${DEFAULT_FETCH_TIMEOUT_MS}ms`);
      }

      // Browser CORS/network rejections commonly surface as TypeError with "Failed to fetch"
      if (ErrorUtilities.isLikelyCors(error)) {
        throw new CorsBlockedError(url);
      }

      // Re-throw preserving message
      throw error instanceof Error ? error : new Error('Unknown image fetch error');
    }

    clearTimeout(timeoutId);

    if (!response?.ok) {
      throw new NetworkError(`HTTP error! status: ${response.status}`, response.status);
    }

    // Check content-type before reading body (faster fail)
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.startsWith('image/')) {
      throw new UnsupportedFormatError('Fetched content is not an image', contentType || 'unknown');
    }

    const blob = await response.blob();

    // Final type check (in case server 'lied' or stripped headers)
    if (!blob.type.startsWith('image/')) {
      throw new UnsupportedFormatError('Fetched content is not an image', blob.type || 'unknown');
    }

    return blob;
  }

  /**
   * Retrieves the dimensions (width and height) of an image from a given URL.
   *
   * @param url - The URL of the image to measure.
   * @returns A Promise that resolves with an object containing the image's width and height.
   * @throws Will reject the promise if the URL is not provided or if the image fails to load.
   */
  static getImageSize(url: string): Promise<{ width: number; height: number }> {
    if (!url) {
      return Promise.reject('URL is required');
    }

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = () => {
        reject(new Error(`Failed to load image from URL: ${url}`));
      };
      img.src = url;
    });
  }

  /**
   * Calculates the aspect ratio of an image given its width and height.
   *
   * @param width - The width of the image.
   * @param height - The height of the image. Must not be zero.
   * @returns The aspect ratio as a number (width divided by height).
   * @throws {Error} Throws an error if the height is zero.
   */
  static calculateAspectRatio(width: number, height: number): number {
    if (height === 0) {
      throw new Error('Height cannot be zero');
    }

    return width / height;
  }

  /**
   * Retrieves the width, height, and aspect ratio of an image from the given URL.
   *
   * @param url - The URL of the image to analyze.
   * @returns A promise that resolves to an object containing the image's width, height, and aspect ratio.
   * @throws Will reject the promise if the image fails to load or URL is invalid.
   */
  static async getImageInfo(url: string): Promise<{ width: number; height: number; aspectRatio: number }> {
    if (!url) {
      return Promise.reject('URL is required');
    }

    try {
      const { width, height } = await this.getImageSize(url);

      return {
        width,
        height,
        aspectRatio: this.calculateAspectRatio(width, height)
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Extracts filename from URL
   * @param url - The URL to extract filename from
   * @returns The extracted filename
   */
  static extractFilenameFromUrl(url: string): string {
    try {
      const urlPath = new URL(url).pathname;
      return urlPath.split('/').pop() || 'image';
    } catch {
      return 'image';
    }
  }

  /**
   * Converts a Blob to a data URL
   * @param blob - The blob to convert
   * @returns Promise that resolves to a data URL string
   */
  private static blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to convert blob to data URL'));
      reader.readAsDataURL(blob);
    });
  }
}
